import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { DataGrid, PageTitleWithToolTip } from '../../Common';
import { StyledCellLink } from '../../Common/DataGrid/CellComponents';
import { getDateRangeFilterOperator } from '../../Common/DataGrid/FilterComponents';
import { select_commonCellProps, text_commonCellProps } from '../../Common/DataGrid/utils';
import { TranslateW } from '../../../Utils/Languages/Translate';
import Routes from '../../../routes';
import { CardHeader } from '@mui/material';
import { isDesktop, postRequestHeaders } from '../../../utils';

const dateRangeFilterOperator = getDateRangeFilterOperator({});

const columns = [
    {
        field: 'workOrderNumber',
        headerName: 'Customer Work Order #',
        ...text_commonCellProps,
        renderCell: ({ row: { workOrderNumber, serviceEventCoolingApplianceId } }) => (
            <StyledCellLink
                path={`/ServiceEvent/${serviceEventCoolingApplianceId}`}
                text={workOrderNumber}
                from={window.location.pathname}
            />
        )
    },
    {
        field: 'transferDate',
        headerName: 'Transfer Date',
        filterOperators: dateRangeFilterOperator
    },
    {
        field: 'materialTypeName',
        headerName: 'Material Type',
        ...select_commonCellProps
    },
    {
        field: 'transferWeight',
        headerName: 'Transfer Weight',
        initiallyVisible: false,
        ...text_commonCellProps
    },
    {
        field: 'transferType',
        headerName: 'Transfer Type',
        initiallyVisible: false,
        ...select_commonCellProps
    },
    {
        field: 'fromAssetName',
        headerName: 'From Asset',
        initiallyVisible: false,
        ...select_commonCellProps,
        renderCell: ({ row: { fromAssetId, fromAssetName } }) => (
            <StyledCellLink
                path={`/Inventory/${fromAssetId}`}
                text={fromAssetName}
                from={window.location.pathname}
            />
        )
    },
    {
        field: 'toAssetName',
        headerName: 'To Asset',
        initiallyVisible: false,
        ...select_commonCellProps,
        renderCell: ({ row: { toAssetId, toAssetName } }) => (
            <StyledCellLink
                path={`/Inventory/${toAssetId}`}
                text={toAssetName}
                from={window.location.pathname}
            />
        )
    },
    {
        field: 'serviceProvider',
        headerName: 'Service Provider',
        ...select_commonCellProps
    },
    {
        field: 'locations',
        headerName: 'Location',
        hideable: false,
        initiallyVisible: false,
        ...select_commonCellProps
    },
    {
        field: 'locationNameAndCode',
        headerName: 'Location Name and Code',
        hideable: false,
        initiallyVisible: false,
        ...text_commonCellProps
    },
    {
        field: 'fromLocationName',
        headerName: 'From Location',
        initiallyVisible: false,
        filterable: false,
        ...text_commonCellProps,
        renderCell: ({ row: { fromLocationId, fromLocationName } }) => (
            isDesktop ? (
                <StyledCellLink
                    path={`/Location/${fromLocationId}`}
                    text={fromLocationName}
                    from={window.location.pathname}
                />
            ) : fromLocationName
        )
    },
    {
        field: 'toLocationName',
        headerName: 'To Location',
        initiallyVisible: false,
        filterable: false,
        ...text_commonCellProps,
        renderCell: ({ row: { toLocationId, toLocationName } }) => (
            isDesktop ? (
                <StyledCellLink
                    path={`/Location/${toLocationId}`}
                    text={toLocationName}
                    from={window.location.pathname}
                />
            ) : toLocationName
        )
    },
    {
        field: 'transferId',
        headerName: 'Transfer ID',
        initiallyVisible: false,
        filterable: false,
        ...text_commonCellProps
    },
]

const MaterialTransferTable = ({ assetID }) => {
    const [tableFilters, setTableFilters] = useState([]);

    const fetchFilters = async () => {
        const response = await fetch(Routes.MaterialTransfers.TableFilter(assetID), {
            method: "POST",
            headers: { ...postRequestHeaders },
        });

        const filters = await response.json();
        setTableFilters(filters);
    };

    useEffect(() => { fetchFilters() }, []);

    return (
        <>
            <DataGrid
                url={Routes.MaterialTransfers.InventoryTable(assetID)}
                exportButtonURL={Routes.MaterialTransfers.InventoryTableExport(assetID)}
                exportButtonAlt={"Export material transfer (.csv)"}
                headerContent={
                    isDesktop && (
                        <CardHeader
                            title={
                                <PageTitleWithToolTip
                                    pageTitle={TranslateW("assetTransfersList.material-transfers")}
                                    toolTipText={TranslateW("assetTransfersList.material-transfer-tooltip")}
                                />
                            }
                            sx={{
                                "& .MuiCardHeader-action": { display: "flex" },
                                "& .MuiCardHeader-content": { maxWidth: "100%", textWrap: "nowrap" }
                            }}
                        />
                    )
                }
                noResultText="Oops...It is empty here..."
                noResultText2="Create a new Material Transfer to get started."
                noResultText3="Create a new Material Transfer change your search criteria."
                filterOptions={tableFilters}
                columns={columns}
            />
        </>

    );
};

MaterialTransferTable.propTypes = {
    assetID: PropTypes.string.isRequired,
};

export default MaterialTransferTable;
